import { Injectable, signal, effect, untracked } from '@angular/core';
import { Router } from '@angular/router';
import { NavController } from '@ionic/angular';
import { PlatformService } from '../platform/platform.service';
import { ReturnUrlService } from '../return-url/return-url.service';
import { Logger, LoggingService } from 'ionic-logging-service';

@Injectable({
  providedIn: 'root'
})
export class AuthSignalService {
  public unauthenticatedSignal = signal(false);
  private logger: Logger;
  private isHandlingUnauthenticated = false;

  constructor(
    private navController: NavController,
    private platformService: PlatformService,
    private returnUrlService: ReturnUrlService,
    private router: Router,
    private loggingService: LoggingService
  ) {
    this.logger = this.loggingService.getLogger("[AuthSignalService]");

    // Watch for unauthenticated signal and handle navigation
    effect(() => {
      if (this.unauthenticatedSignal() && !this.isHandlingUnauthenticated) {
        this.handleUnauthenticatedUser();
      }
    });
  }

  public triggerUnauthenticated(): void {
    this.logger.info('triggerUnauthenticated() - User needs to be redirected to sign-in');
    if (!this.isHandlingUnauthenticated) {
      this.unauthenticatedSignal.set(true);
    }
  }

  /**
   * Replaces the whole document, so AuthPage bootstraps from scratch.
   *
   * Its own method purely as a test seam: assigning window.location.href in a spec would
   * navigate the Karma runner away mid-suite.
   *
   * @param url - Where to reload to
   */
  protected forceFullLoad(url: string): void {
    window.location.href = url;
  }

  private handleUnauthenticatedUser(): void {
    this.logger.info('handleUnauthenticatedUser() - Navigating to sign-in');

    // Prevent re-entry
    this.isHandlingUnauthenticated = true;

    // Reset the signal outside of the reactive context to prevent loops
    untracked(() => {
      this.unauthenticatedSignal.set(false);
    });

    this.returnUrlService.stash(this.router.url);

    // ACCESS_DENIED is kept here, unlike authGuard: a 401/403 already rejected the stored
    // credentials, so re-verifying them would only fail again.
    const isCordova = this.platformService.isCordova();
    const queryParams = { signOutParam: 'ACCESS_DENIED' };
    const path = isCordova ? '/sign-in' : '/auth';

    // Web, already on /auth: IonicRouteStrategy reuses the component, so ngOnInit ->
    // setQueryParams never re-runs and AuthPage neither explains the ejection nor hands off —
    // leaving a blank page, since every block in its template is conditional. Replace the
    // document instead, as MenuComponent.navigateToSignOut() does for sign-out. Native needs no
    // equivalent: LoginPage reacts to the parameter on a reused component.
    //
    // Both separators: the tenant-hub landing is "/auth#refreshToken=…".
    if (!isCordova && this.router.url.split(/[?#]/)[0] === '/auth') {
      this.logger.info('handleUnauthenticatedUser() - already on /auth, forcing a fresh load');
      // The latch stays set, unlike the navigateRoot path below: location.href only *queues*
      // the load, and a dead session rejects several requests at once, so releasing it would let
      // the next 401 restart the navigation. The next instance constructs with false anyway.
      this.forceFullLoad(`/auth?signOutParam=${queryParams.signOutParam}`);
      return;
    }

    this.navController.navigateRoot([path], { queryParams }).then(() => {
      // Reset the flag after navigation completes
      this.isHandlingUnauthenticated = false;
    }).catch((error) => {
      this.logger.error('Navigation error:', error);
      this.isHandlingUnauthenticated = false;
    });
  }
}